import { useEffect, useState } from "react";
import client from "../lib/api";
import { toast, Toaster } from "sonner";
import { Slider } from "../components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { marketPill, scoreChipClass } from "../lib/format";

const KEYS = [
  { k: "size_vs_holding", label: "Transaction Size vs Insider Holding" },
  { k: "value_vs_market_cap", label: "Absolute Value vs Market Cap" },
  { k: "role", label: "Insider Role Weight" },
  { k: "cluster", label: "Cluster Factor (10-day window)" },
  { k: "timing", label: "Direction × Timing vs Recent Price" },
];

export default function Settings() {
  const [w, setW] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [diff, setDiff] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    client.get("/settings/weights").then((r) => setW(r.data));
  }, []);

  const total = w ? Object.values(w).reduce((a, b) => a + b, 0) : 0;
  const totalOk = Math.abs(total - 1.0) < 0.01;

  const update = (k, val) => {
    setW({ ...w, [k]: val });
    setDirty(true);
  };

  const save = async () => {
    if (!totalOk) { toast.error(`Weights must sum to 1.00 (currently ${total.toFixed(2)})`); return; }
    setSaving(true);
    try {
      const { data } = await client.put("/settings/weights", w);
      toast.success(`Saved · new version ${data.version.slice(0, 12)} · rescored`);
      setDirty(false);
      setDiff(data.diff);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!w) return <div className="p-6 mono terminal-loader">Loading</div>;

  return (
    <div className="max-w-4xl" data-testid="settings-page">
      <Toaster theme="dark" position="top-right" />
      <h2 className="text-[22px] font-semibold tracking-tight mb-2">Score weight configuration</h2>
      <div className="text-[13px] text-[color:var(--text-tertiary)] mb-7">
        Saving new weights creates a new score version. Historical scores are preserved per transaction under the previous version.
      </div>

      <div className="glass overflow-hidden">
        {KEYS.map(({ k, label }, i) => (
          <div key={k} className={`p-6 ${i < KEYS.length - 1 ? "border-b border-white/[0.04]" : ""}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[14px] font-medium">{label}</div>
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] mt-1 mono">{k}</div>
              </div>
              <div className="mono text-[28px] font-semibold grad-text" data-testid={`weight-${k}`}>{(w[k] * 100).toFixed(0)}%</div>
            </div>
            <Slider
              value={[w[k] * 100]}
              onValueChange={(v) => update(k, v[0] / 100)}
              min={0}
              max={60}
              step={1}
              data-testid={`slider-${k}`}
              className="w-full"
            />
          </div>
        ))}
      </div>

      <div className="mt-4 glass flex items-center justify-between p-5">
        <div className="mono">
          <span className="text-[color:var(--text-tertiary)] uppercase text-[10px] tracking-wider mr-3 font-medium">Total</span>
          <span className="text-2xl font-semibold" style={{ color: totalOk ? "var(--score-high)" : "var(--lemon)" }} data-testid="weight-total">{(total * 100).toFixed(0)}%</span>
          {!totalOk && <span className="ml-3 text-[color:var(--lemon)] text-xs">Must equal 100%</span>}
        </div>
        <button
          onClick={save}
          disabled={!dirty || !totalOk || saving}
          data-testid="save-weights-btn"
          className="btn-primary px-7 py-3"
        >
          {saving ? "Saving…" : "Save & Rescore All"}
        </button>
      </div>

      <DiffDialog diff={diff} onClose={() => setDiff(null)} onNav={(id) => { setDiff(null); nav(`/tx/${id}`); }} />
    </div>
  );
}

function DiffStatBox({ label, value, color, grad }) {
  return (
    <div className="rounded-xl p-3 bg-black/25 border border-white/[0.06]">
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)] font-medium">{label}</div>
      <div className={`mono text-[26px] font-semibold mt-1 leading-none ${grad ? "grad-text" : ""}`} style={{ color: !grad && color ? color : undefined }}>{value}</div>
    </div>
  );
}

function DiffDialog({ diff, onClose, onNav }) {
  if (!diff) return null;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="glass rounded-3xl max-w-3xl border-white/[0.06]" data-testid="diff-dialog">
        <DialogHeader><DialogTitle className="text-[15px] font-semibold">Rescore · Impact Report · <span className="mono text-[color:var(--cyan)]">{diff.version.slice(0, 12)}</span></DialogTitle></DialogHeader>
        <div className="grid grid-cols-4 gap-2 mb-4">
          <DiffStatBox label="→ Surfaced" value={diff.moved_to_surfaced} color="var(--score-high)" />
          <DiffStatBox label="→ Review" value={diff.moved_to_review} color="var(--lemon)" />
          <DiffStatBox label="→ Hidden" value={diff.moved_to_hidden} color="var(--pink)" />
          <DiffStatBox label="Unchanged" value={diff.unchanged} color="var(--text-secondary)" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <MoverPane title="Biggest gainers" rows={diff.biggest_gainers} onNav={onNav} testid="movers-up" />
          <MoverPane title="Biggest losers" rows={diff.biggest_losers} onNav={onNav} testid="movers-down" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-primary" data-testid="diff-close">Done</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoverPane({ title, rows, onNav, testid }) {
  return (
    <div className="rounded-xl bg-black/25 border border-white/[0.06] overflow-hidden" data-testid={testid}>
      <div className="data-header">{title}</div>
      {rows.length === 0 && <div className="data-cell mono text-[color:var(--text-tertiary)]">—</div>}
      {rows.map((r) => (
        <div key={r.id} onClick={() => onNav(r.id)} className="grid grid-cols-[70px_1fr_60px_60px_70px] border-t border-white/[0.04] hover:bg-white/[0.03] cursor-pointer items-center" data-testid={`mover-${r.ticker}`}>
          <div className="data-cell"><span className={marketPill(r.market)}>{r.market}</span></div>
          <div className="data-cell mono font-semibold text-[color:var(--cyan)]">{r.ticker}</div>
          <div className="data-cell mono text-[11px] text-[color:var(--text-tertiary)] text-right">{(r.prev_score * 100).toFixed(0)}</div>
          <div className="data-cell text-right"><span className={scoreChipClass(r.new_score)} style={{ minWidth: 40, padding: "3px 8px", fontSize: 12 }}>{(r.new_score * 100).toFixed(0)}</span></div>
          <div className="data-cell mono font-semibold text-right" style={{ color: r.delta >= 0 ? "var(--score-high)" : "var(--pink)" }}>{r.delta >= 0 ? "+" : ""}{(r.delta * 100).toFixed(1)}</div>
        </div>
      ))}
    </div>
  );
}
